import React from 'react';
import { BarChart, TrendingDown, ArrowRightLeft } from 'lucide-react';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент для визуализации сравнения текущего и нового кредита
 */
const ComparisonChart = ({ comparisonData }) => {
  if (!comparisonData || !comparisonData.datasets) return null;

  // Извлекаем данные для графика
  const { labels, datasets, monthlySavings, totalSavings, effectiveness } = comparisonData;

  // Итоговые суммы для каждого кредита
  const currentLoanTotal = datasets.reduce((sum, dataset) => sum + dataset.data[0], 0);
  const newLoanTotal = datasets.reduce((sum, dataset) => sum + dataset.data[1], 0);
  
  // Найдем максимальную высоту для шкалы
  const maxAmount = Math.max(currentLoanTotal, newLoanTotal);
  const scale = 100 / maxAmount; // Масштаб для процентной высоты
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart className="w-5 h-5 text-indigo-500" />
          Сравнение стоимости кредитов
        </h3>
      </div>

      <div className="space-y-4">
        {/* Визуализация сравнения кредитов */}
        <div className="flex flex-col sm:flex-row justify-between gap-6 mb-4">
          {labels.map((label, index) => (
            <div key={label} className="flex-1 flex flex-col items-center">
              <div className="w-full h-64 bg-gray-100 dark:bg-gray-700 rounded-lg relative overflow-hidden">
                {/* Стек столбца */}
                {datasets.map((dataset, dataIndex) => {
                  const height = dataset.data[index] * scale;
                  const prevHeight = datasets
                    .slice(0, dataIndex)
                    .reduce((sum, ds) => sum + ds.data[index] * scale, 0);
                  
                  return (
                    <div
                      key={`${label}-${dataIndex}`}
                      className="absolute w-full transition-all duration-500 ease-in-out"
                      style={{
                        height: `${height}%`,
                        bottom: `${prevHeight}%`,
                        backgroundColor: dataset.backgroundColor[index],
                      }}
                    >
                      {/* Метка данных внутри столбца */}
                      {height > 10 && (
                        <div className="p-2 text-center text-white text-xs">
                          <span className="font-semibold">{formatCurrency(dataset.data[index])}</span>
                          <div className="text-xs opacity-80">{dataset.label}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Общая сумма над столбцом */}
                <div className="absolute -top-8 left-0 right-0 text-center font-semibold">
                  {formatCurrency(index === 0 ? currentLoanTotal : newLoanTotal)}
                </div>
              </div>
              <div className="text-center mt-3 font-medium text-gray-800 dark:text-gray-200">
                {label}
              </div>
            </div>
          ))}
        </div>

        {/* Сравнение и экономия */}
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Ежемесячная экономия */}
            <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-100 dark:border-blue-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                <TrendingDown className="w-4 h-4" />
                Ежемесячная экономия
              </div>
              <div className="text-xl font-bold text-blue-800 dark:text-blue-200">
                {monthlySavings > 0 ? formatCurrency(monthlySavings) : "0 ₽"}
              </div>
            </div>
            
            {/* Общая экономия */}
            <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-lg border border-green-100 dark:border-green-800/50">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-300 mb-1">
                <ArrowRightLeft className="w-4 h-4" />
                Общая экономия
              </div>
              <div className="text-xl font-bold text-green-800 dark:text-green-200">
                {totalSavings > 0 ? formatCurrency(totalSavings) : "0 ₽"}
              </div>
            </div>
            
            {/* Эффективность */}
            <div className={`p-3 rounded-lg border ${
              effectiveness === 'Невыгодно' 
                ? 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800/50' 
                : 'bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/50'
            }`}>
              <div className={`flex items-center gap-2 text-sm font-medium mb-1 ${
                effectiveness === 'Невыгодно'
                  ? 'text-red-700 dark:text-red-300'
                  : 'text-green-700 dark:text-green-300'
              }`}>
                <BarChart className="w-4 h-4" />
                Эффективность
              </div>
              <div className={`text-xl font-bold ${
                effectiveness === 'Невыгодно'
                  ? 'text-red-800 dark:text-red-200'
                  : 'text-green-800 dark:text-green-200'
              }`}>
                {effectiveness}
              </div>
            </div>
          </div>
        </div>

        {/* Легенда */}
        <div className="flex flex-wrap justify-center gap-4 pt-2">
          {datasets.map((dataset, idx) => (
            <div key={idx} className="flex items-center">
              <div 
                className="w-4 h-4 mr-1 rounded-sm" 
                style={{ backgroundColor: dataset.backgroundColor[0] }}
              ></div>
              <span className="text-sm text-gray-700 dark:text-gray-300">{dataset.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ComparisonChart;